/**
 * @lines98/client/lib/bg-layer — ОДИН общий painted fixed-фон-слой приложения.
 *
 * ЗАЧЕМ / WHY THIS EXISTS: тема-градиент (`--bg-grad`) раньше жил на `body` и
 * привязывался к вьюпорту через `background-attachment: fixed` (product-wide приём
 * против white-flash при переходах). На mobile-webkit `background-attachment:fixed`
 * печально известен repaint-лагом при инерционном momentum-скролле: зафиксированный
 * к вьюпорту фон не успевает перерисоваться под непрозрачной плашкой карточки
 * (`.mode-card { background: var(--panel) }`) → видна «белая полоса», тянущаяся за
 * плашкой; на release она исчезает (owner-баг 2026-06-24, стартовая). Приём к тому же
 * был ПРОДУБЛИРОВАН в 3 CSS-файлах (global.css / start.css / profile.css) — фрагментация.
 *
 * РЕШЕНИЕ (вариант A тикета frontend-lead 2026-06-24
 * `docs/orchestrator/agent-outputs/2026-06-24_frontend-lead_start-page-white-scroll-strip.md`):
 * выделенный РЕАЛЬНЫЙ painted DOM-узел `position:fixed; inset:0; z-index:-1`, несущий
 * `var(--bg-grad)` (объявление слоя — в global.css, единый источник). Это painted layer,
 * а не attachment-трюк → robust против mobile-webkit repaint и НЕ возвращает white-flash
 * (слой покрывает весь вьюпорт на каждом кадре скролла И перехода). `background-attachment:
 * fixed` убран со всех трёх мест; body несёт лишь сплошной `var(--bg)` как base под слоем.
 *
 * SCOPE: создаёт/монтирует ОДИН узел `[data-testid="bg-layer"]` в шелле приложения
 *   (ствол, не per-screen — модель «ствол→ветки»). Идемпотентно (повторный вызов не плодит
 *   дублей). Узел — ПЕРЕД `#app` (z-index:-1 кладёт его позади контента в любом случае).
 * NOT IN SCOPE: цвет/градиент (живёт в токене темы `--bg-grad`, retro-theme.css/ADR-0020);
 *   размеры (слой viewport-относителен через `inset:0` — без капов, §0.G.5); reveal-gate
 *   стартовой (start-screen.ts — не трогается).
 *
 * Cross-references: тикет AC-1..AC-9; styles/global.css (`[data-testid="bg-layer"]`
 *   правило + удалённые 3 дубля `background-attachment:fixed`); responsive-ui §0.G.5
 *   (нет верхних капов) / §0.H (токены) / §0.A (visibility, слой ПОД контентом);
 *   code-structure §1/§3 (общий модуль, не дубль по экранам).
 */

/** Стабильный хук для тестов и для идемпотентности (один узел на приложение). */
const BG_LAYER_TESTID = 'bg-layer';

/**
 * Смонтировать общий фон-слой ОДИН раз в шелле (вызывается на boot из main.ts, до
 * первого mount экрана — чтобы фон покрывал вьюпорт уже к первому кадру/reveal, AC-3).
 *
 * Узел вставляется ПЕРЕД `#app` (host), но визуальный порядок задаёт `z-index:-1`
 * (CSS в global.css) → слой всегда позади контента, независимо от точки вставки.
 * Идемпотентно: если узел уже есть (HMR / повторный boot) — повторно не создаём.
 *
 * @param host — корневой контейнер приложения (`#app`); слой кладётся как его sibling.
 * @returns смонтированный (или уже существующий) узел слоя.
 */
export function mountBgLayer(host: HTMLElement): HTMLElement {
  const existing = document.querySelector<HTMLElement>(`[data-testid="${BG_LAYER_TESTID}"]`);
  if (existing) return existing;

  const layer = document.createElement('div');
  layer.dataset.testid = BG_LAYER_TESTID;
  // Декоративный слой — скрыт от скринридеров (§8 a11y: чисто визуальный фон).
  layer.setAttribute('aria-hidden', 'true');
  // Геометрия и градиент — целиком в CSS (global.css `[data-testid="bg-layer"]`):
  // единый источник, без inline-хардкода (§4 no-hardcode). Здесь — только монтаж узла.

  // Вставляем ПЕРЕД #app, в начало body — порядок в DOM не критичен (z-index:-1 решает),
  // но «фон первым» читается естественно и гарантирует, что слой существует к reveal.
  const parent = host.parentElement ?? document.body;
  parent.insertBefore(layer, host);
  return layer;
}
